import { useTranslation } from '../../../lib/i18n';
import { Button, Typography } from '../ui';
import styles from './DocViewerModal.module.css';

// The viewer's three non-document states, shared by `DocViewerBody` (markdown/html/csv/text) and
// `PdfDocView` (which resolves its own bytes and so owns its own copies of them).
//
// Extracted rather than duplicated because the INVARIANT is what matters: the viewer never renders nothing.
// Every failure path lands on one of these three, and `test/doc-viewer-wiring.test.ts` asserts both hosts
// reach all of them. Two flavours of "something went wrong" in one viewer is how one of them quietly loses
// its Retry.

/** Loading. A skeleton of a page rather than a spinner — the wait is for a document, so the shape should
 *  promise one. */
export function DocShimmer() {
  return (
    <div className={styles.shimmerLines} aria-hidden>
      {['70%', '92%', '84%'].map((width) => (
        <div key={width} className={`orion-shimmer ${styles.shimmerLine}`} style={{ width }} />
      ))}
      <div className={`orion-shimmer ${styles.shimmerBlock}`} />
    </div>
  );
}

/** A NORMAL outcome, stated and nothing more: this file type has no preview.
 *
 *  Deliberately button-less, unlike `HandOffState` below. It is not a failure — a `.docx` was never going to
 *  render — and the shell's footer is already showing Download / Open in new tab / Discuss two lines beneath
 *  it. Repeating Download here put a black primary button directly above an outlined one that did the same
 *  thing, which reads as a mistake.
 *
 *  The error states keep their own button, and that difference is the point: a failure deserves a prominent
 *  recovery, an expected outcome does not. */
export function DocNoticeState({ message }: { message: string }) {
  return (
    <div className={styles.state}>
      <Typography variant="p2" color="secondary" component="p">
        {message}
      </Typography>
    </div>
  );
}

/** A dead end with a way out: the states that CANNOT be recovered by trying again — "too large",
 *  "unavailable", "this browser won't render a PDF" — say so and offer the hand-off, never a Retry. */
export function HandOffState({ message, onDownload }: { message: string; onDownload: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.state}>
      <Typography variant="p2" color="secondary" component="p">
        {message}
      </Typography>
      <Button onClick={onDownload}>{t('packs.download')}</Button>
    </div>
  );
}

/** A failure that MIGHT be transient, so Retry is the whole point. `retry: false` is the app-wide React Query
 *  default, which is exactly why this button has to exist — nothing else will try again. */
export function DocErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.state}>
      <Typography variant="p2" color="secondary" component="p">
        {t('docViewer.error')}
      </Typography>
      <Button onClick={onRetry}>{t('docViewer.retry')}</Button>
    </div>
  );
}
